import { apply_patch } from 'jsonpatch';

import { NotebookRunState } from '@mode/legacy/types-app-state';
import { combineAllReducers } from '@mode/legacy/util-domain';
import { EmbedReportActions, EmbedReportState } from '@mode/reports/sandbox/contract';
import { getCustomTableConfigMapFromUserSettingsArray } from '@mode/shared/util-js';

export const initialEmbedReportState: EmbedReportState.ReportFeatureState = {
  serializedReport: {
    account: {
      embedded: false,
      isThoughtspotIntegrated: false,
      loggedIn: false,
      whiteLabel: false,
    },
    report: {
      token: '',
      layout: null,
      name: null,
      description: null,
      fullWidth: false,
      theme: {
        href: '',
        customSource: '',
      },
      drillAnywhereEnabled: false,
      hasImportedDatasets: false,
    },
    queryRunStatus: {},
    charts: {
      tokens: [],
      versions: {},
      queryTokens: {},

      titles: {},
      descriptions: {},

      captions: {},

      chartTypes: {},
      encodings: {},
      formats: {},
      fieldFormats: {},
      fieldColors: {},
      colorPaletteTokens: {},
      facetSizes: {},
      isAutoUpdateDisabled: {},

      v1Views: {},
      histories: {},

      runLinks: {},
      vizLinks: {},
    },
    tables: {},
    legacyEmbedDatasets: {},
    datasetResults: [],
    localQueryTokensSortedByCreationDate: [],
    notebookVisuals: {
      runState: NotebookRunState.Uninitialized,
      titles: {},
      vizTokens: [],
      cellTokens: {},
      contents: {},
      resultTypes: {},
      dates: {},
      contentTypes: {},
      vizLinks: {},
      vizRunLinks: {},
      missingResults: {},
    },
    filters: {
      controls: [],
      options: {},
      totalOptionCount: {},
      selectedOptionCount: {},
      all: {},
    },
    ui: {
      filterPanelOpen: false,
      captureReportRunCreatedAt: null,
    },
    reportLevelColorMaps: {},
    features: [],
    flamingo: {
      recordsByViz: {},
      views: {},
      pivotsByViz: {},
      columnsByViz: {},
      statesByViz: {},
      errorsByViz: {},
      dataProfiling: {},
      conditionalFormatting: {},
    },
    inViewport: {},
    downloadStatus: {},
  },
  themeLoaded: false,
  alamodeCustomSettings: {},
};

function serializedReportReducer(
  state = initialEmbedReportState.serializedReport,
  action: EmbedReportActions.Actions
): EmbedReportState.SerializedReportState {
  switch (action.type) {
    case EmbedReportActions.Type.UpdateState:
      try {
        return apply_patch(state, action.delta);
      } catch (err) {
        break;
      }
    case EmbedReportActions.Type.LoadState:
      return action.state;
    case EmbedReportActions.Type.ResetState:
      const serialized = initialEmbedReportState.serializedReport;
      // Don't need to reset everything to prevent unnecessary component changes
      return {
        ...serialized,
        report: {
          ...state.report,
          layout: action.reportLayout,
        },
        ui: {
          ...serialized.ui,
          filterPanelOpen: state.ui.filterPanelOpen,
        },
      };
  }
  return state;
}

function themeLoadedReducer(
  state = initialEmbedReportState.themeLoaded,
  action: EmbedReportActions.Actions
): EmbedReportState.ReportFeatureState['themeLoaded'] {
  switch (action.type) {
    case EmbedReportActions.Type.LoadThemeSuccess:
      return true;
  }

  return state;
}

function alamodeCustomSettingsReducer(
  state = initialEmbedReportState.alamodeCustomSettings,
  action: EmbedReportActions.Actions
): EmbedReportState.ReportFeatureState['alamodeCustomSettings'] {
  switch (action.type) {
    case EmbedReportActions.Type.AlamodeCustomizeTable:
      const validConfiguration = getCustomTableConfigMapFromUserSettingsArray(action.payload);
      if (Object.keys(validConfiguration).length > 0) {
        return {
          ...state,
          ...validConfiguration,
        };
      }

      return state;
  }

  return state;
}

export const EmbedViewerRedurcers = {
  serializedReport: serializedReportReducer,
  themeLoaded: themeLoadedReducer,
  alamodeCustomSettings: alamodeCustomSettingsReducer,
};

export const embedReportReducers = combineAllReducers<
  EmbedReportState.FeatureTree,
  keyof EmbedReportState.FeatureTree,
  EmbedReportActions.Actions
>({
  reportFeature: EmbedViewerRedurcers,
});
